import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import {
  ageDetails, dateDifference, parseDateInput, shiftDate, toDateInput, SHIFT_UNITS,
} from '../../lib/dates';
import { fieldClass, labelClass, mutedClass, panelClass, resultClass, tabClass } from '../uiStyles';

const TABS = ['age', 'difference', 'shift'];

const Stat = ({ label, value, isDarkMode }) => (
  <div className={`rounded-lg px-3 py-2 ${isDarkMode ? 'bg-dark/50' : 'bg-white'}`}>
    <p className={`text-xs ${mutedClass(isDarkMode)}`}>{label}</p>
    <p className={`text-lg font-semibold ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>{value}</p>
  </div>
);

const DateCalculator = () => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const today = useMemo(() => new Date(), []);

  const [tab, setTab] = useState('age');
  const [birth, setBirth] = useState('1992-03-15');
  const [range, setRange] = useState([toDateInput(today), toDateInput(shiftDate(today, 30, 'days'))]);
  const [shift, setShift] = useState({ date: toDateInput(today), amount: '30', unit: 'days' });

  const formatDate = (date) => date.toLocaleDateString(i18n.language, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const formatNumber = (value) => value.toLocaleString(i18n.language);
  const weekdayName = (index) => new Date(2024, 0, 7 + index)
    .toLocaleDateString(i18n.language, { weekday: 'long' });

  const age = useMemo(() => {
    const parsed = parseDateInput(birth);
    return parsed ? ageDetails(parsed, today) : null;
  }, [birth, today]);

  const difference = useMemo(() => {
    const from = parseDateInput(range[0]);
    const to = parseDateInput(range[1]);
    return from && to ? dateDifference(from, to) : null;
  }, [range]);

  const shifted = useMemo(() => {
    const parsed = parseDateInput(shift.date);
    const amount = Number(shift.amount);
    return parsed && Number.isFinite(amount) ? shiftDate(parsed, amount, shift.unit) : null;
  }, [shift]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={tabClass(isDarkMode, tab === key)}
          >
            {t(`useful.date.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'age' && (
        <div className="space-y-4">
          <div className="max-w-xs">
            <label className={labelClass(isDarkMode)} htmlFor="date-birth">
              {t('useful.date.birthDate')}
            </label>
            <input
              id="date-birth"
              type="date"
              value={birth}
              max={toDateInput(today)}
              onChange={(event) => setBirth(event.target.value)}
              className={fieldClass(isDarkMode)}
            />
          </div>

          {age ? (
            <div className={`${panelClass(isDarkMode)} space-y-3`}>
              <p className={resultClass(isDarkMode)}>
                {t('useful.date.ageValue', { years: age.years, months: age.months, days: age.days })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label={t('useful.date.totalDays')} value={formatNumber(age.totalDays)} isDarkMode={isDarkMode} />
                <Stat label={t('useful.date.totalWeeks')} value={formatNumber(age.totalWeeks)} isDarkMode={isDarkMode} />
                <Stat label={t('useful.date.totalMonths')} value={formatNumber(age.totalMonths)} isDarkMode={isDarkMode} />
                <Stat label={t('useful.date.totalHours')} value={formatNumber(age.totalHours)} isDarkMode={isDarkMode} />
              </div>
              <p className={`text-sm ${mutedClass(isDarkMode)}`}>
                {t('useful.date.bornOn', { weekday: weekdayName(age.birthWeekday) })} · {' '}
                {age.daysToBirthday === 0
                  ? t('useful.date.birthdayToday')
                  : t('useful.date.nextBirthday', {
                    days: age.daysToBirthday,
                    date: formatDate(age.nextBirthday),
                  })}
              </p>
            </div>
          ) : (
            <p className="text-sm text-amber-500">{t('useful.date.invalid')}</p>
          )}
        </div>
      )}

      {tab === 'difference' && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
            <div>
              <label className={labelClass(isDarkMode)} htmlFor="date-from">{t('useful.date.from')}</label>
              <input
                id="date-from"
                type="date"
                value={range[0]}
                onChange={(event) => setRange([event.target.value, range[1]])}
                className={fieldClass(isDarkMode)}
              />
            </div>
            <div>
              <label className={labelClass(isDarkMode)} htmlFor="date-to">{t('useful.date.to')}</label>
              <input
                id="date-to"
                type="date"
                value={range[1]}
                onChange={(event) => setRange([range[0], event.target.value])}
                className={fieldClass(isDarkMode)}
              />
            </div>
          </div>

          {difference ? (
            <div className={`${panelClass(isDarkMode)} space-y-3`}>
              <p className={resultClass(isDarkMode)}>
                {t('useful.date.dayCount', { count: difference.totalDays })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat
                  label={t('useful.date.calendarSpan')}
                  value={t('useful.date.ageValue', difference)}
                  isDarkMode={isDarkMode}
                />
                <Stat
                  label={t('useful.date.totalWeeks')}
                  value={`${difference.totalWeeks} + ${difference.remainderDays}`}
                  isDarkMode={isDarkMode}
                />
                <Stat label={t('useful.date.weekdays')} value={formatNumber(difference.weekdays)} isDarkMode={isDarkMode} />
                <Stat label={t('useful.date.weekend')} value={formatNumber(difference.weekendDays)} isDarkMode={isDarkMode} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-500">{t('useful.date.invalid')}</p>
          )}
        </div>
      )}

      {tab === 'shift' && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3 max-w-2xl">
            <div>
              <label className={labelClass(isDarkMode)} htmlFor="shift-date">{t('useful.date.startDate')}</label>
              <input
                id="shift-date"
                type="date"
                value={shift.date}
                onChange={(event) => setShift({ ...shift, date: event.target.value })}
                className={fieldClass(isDarkMode)}
              />
            </div>
            <div>
              <label className={labelClass(isDarkMode)} htmlFor="shift-amount">{t('useful.date.amount')}</label>
              <input
                id="shift-amount"
                type="number"
                value={shift.amount}
                onChange={(event) => setShift({ ...shift, amount: event.target.value })}
                className={fieldClass(isDarkMode)}
              />
            </div>
            <div>
              <label className={labelClass(isDarkMode)} htmlFor="shift-unit">{t('useful.date.unit')}</label>
              <select
                id="shift-unit"
                value={shift.unit}
                onChange={(event) => setShift({ ...shift, unit: event.target.value })}
                className={fieldClass(isDarkMode)}
              >
                {SHIFT_UNITS.map((unit) => (
                  <option key={unit} value={unit}>{t(`useful.date.units.${unit}`)}</option>
                ))}
              </select>
            </div>
          </div>
          <p className={`text-xs ${mutedClass(isDarkMode)}`}>{t('useful.date.shiftHint')}</p>

          {shifted ? (
            <div className={panelClass(isDarkMode)}>
              <p className={resultClass(isDarkMode)}>{formatDate(shifted)}</p>
              <p className={`text-sm mt-2 ${mutedClass(isDarkMode)}`}>
                {weekdayName(shifted.getDay())} · {toDateInput(shifted)}
              </p>
            </div>
          ) : (
            <p className="text-sm text-amber-500">{t('useful.date.invalid')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default DateCalculator;
